import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { logger } from '../../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { sessionInfoAtom } from '../../state';
import { normalizeVideoPlatform } from '../../video-sdk';
import {
  NewSessionConfirmationVariation,
  newSessionConfirmationDataAtom,
  providerShortIdAtom,
  waitingVisitorsAtom,
} from '../state';
import { StartNewSessionConfirmationModal } from './StartNewSessionConfirmationModal';
import { useStartNewClientSessionCallback } from './useStartNewClientSessionCallback';

/**
 * Emits a Segment event when the user views the incompatible variation of the
 * new session confirmation modal.
 */
function useTrackIncompatibleVariationViewed(variation?: NewSessionConfirmationVariation, patientShortId?: string) {
  const { userViewed } = useTrackEvent();
  const getAppointmentShortId = useAtomCallbackMemoized((get) => get(sessionInfoAtom)?.appointment?.shortId, []);
  const getProviderShortId = useAtomCallbackMemoized((get) => get(providerShortIdAtom), []);
  const getVideoPlatform = useVideoPlatformCallback();
  const getWaitingVisitors = useAtomCallbackMemoized((get) => get(waitingVisitorsAtom), []);

  useEffect(
    function trackUserViewedIncompatibleVariation() {
      const providerShortId = getProviderShortId();
      const providerVideoPlatform = normalizeVideoPlatform(getVideoPlatform());
      if (variation === 'incompatible' && patientShortId && providerShortId) {
        logger.warn('Application compatibility issue detected', {
          clientVideoPlatforms: Object.values(getWaitingVisitors()).map((client) => client.videoPlatform),
          providerVideoPlatform,
        });
        userViewed(EventTypeEnum.VIDEO_PLATFORM_COMPATIBILITY_WARNING_VIEWED, {
          patientShortId,
          providerShortId: providerShortId,
          source: providerVideoPlatform,
          appointmentShortId: getAppointmentShortId(),
        });
      }
    },
    [
      userViewed,
      variation,
      patientShortId,
      getProviderShortId,
      getVideoPlatform,
      getAppointmentShortId,
      getWaitingVisitors,
    ],
  );
}

export function StartNewSessionConfirmationModalWrapper() {
  const [newSessionConfirmationData, setNewSessionConfirmationData] = useAtom(newSessionConfirmationDataAtom);
  const [onStartNew, { loading: isCompletingSession }] = useStartNewClientSessionCallback();
  const { visitorName, visitorUuid, patientShortId, variation } = newSessionConfirmationData ?? {};

  useTrackIncompatibleVariationViewed(variation, patientShortId);

  return (
    <StartNewSessionConfirmationModal
      isOpen={!!newSessionConfirmationData}
      loading={isCompletingSession}
      onClose={() => {
        setNewSessionConfirmationData(null);
      }}
      onConfirm={async () => {
        if (!visitorUuid || !visitorName || !patientShortId) return;
        await onStartNew(visitorUuid, visitorName, patientShortId);
        sendLoggingEvents(TrackingEvents.PROVIDER_END_AND_START_NEW_SESSION);
        setNewSessionConfirmationData(null);
      }}
      visitorName={visitorName}
      variation={variation}
    />
  );
}
